import { filterCategories } from "@/data/filters/config";
import {
  configurationSchema,
  newScheduleSchema,
  stopListSchema,
  type Configuration,
  type DepartmentSizeQueryFilter,
  type EventQueryFilter,
  type QueryFilter,
} from "@/types/campaign";
import { AddUndefinedUnionProps } from "@/types/union-props";
import { createMigrationManager } from "@/utils/zod-json-migratable";
import { CalendarDate, parseDate } from "@internationalized/date";
import * as Sentry from "@sentry/react";
import { atomWithStorage, createJSONStorage } from "jotai/utils";
import { SyncStorage } from "jotai/vanilla/utils/atomWithStorage";
import { omit } from "lodash-es";
import z from "zod";

function zodAddUndefinedUnionProps<T extends z.ZodType>(t: T) {
  return t as z.ZodType<
    AddUndefinedUnionProps<z.output<T>>,
    AddUndefinedUnionProps<z.input<T>>
  >;
}

const calendarDateCodec = z.codec(
  z.iso.date(),
  z.custom<CalendarDate>((value) => value instanceof CalendarDate),
  {
    decode: (isoString) => parseDate(isoString),
    encode: (calendarDate) => calendarDate.toString(),
  },
);

const stringSetCodec = z.codec(z.array(z.string()), z.set(z.string()), {
  decode: (array) => new Set(array),
  encode: (set) => [...set],
});

const lastVisitedStepMapCodec = z.codec(
  z.array(z.tuple([z.string().nullable(), z.string()])),
  z.map(z.string().nullable(), z.string()),
  {
    decode: (array) => new Map(array),
    encode: (map) => [...map],
  },
);

const withVersionNumber = <V extends number, T extends z.ZodRawShape>(
  version: V,
  schema: z.ZodObject<T>,
) =>
  z.object({
    ...omit(schema.shape, ["__version"]),
    __version: z.literal(version),
  });

/**
 * DO NOT CHANGE THIS SCHEMA EVER.
 * If we need to make changes to NewCampaignState, we do it via migrations, and we add new versions of the schema with the changes,
 * but we never change the existing schema, as it represents the shape of the data at version 1, and changing it would break the migrations.
 */
const newCampaignStateSchemaBase = withVersionNumber(
  1,
  z.object({
    draftId: z.string(),
    sessionId: z.string(),
    isSetup: z.boolean(),
    name: z.string().nullable(),
    configurations: z.array(configurationSchema),
    stopLists: z.array(stopListSchema).nullable(),
    responsibleId: z.string().nullable(),
    planning: zodAddUndefinedUnionProps(
      z
        .union([
          z.object({
            type: z.literal("Scheduled"),
            schedules: z.array(newScheduleSchema),
            chosenStartDate: calendarDateCodec.nullable(),
            chosenEndDate: calendarDateCodec.nullable(),
          }),
          z.object({
            type: z.literal("Triggered"),
            maxLeadsPerWeek: z.number().nullable(),
            triggers: z.array(
              z.object({
                event: z.string(),
              }),
            ),
            chosenStartDate: calendarDateCodec.nullable(),
            chosenEndDate: calendarDateCodec.nullable(),
          }),
          z.object({
            type: z.literal("OnDemand"),
            leadAmount: z.number().nullable(),
          }),
        ])
        .and(
          z.object({
            deliverContacts: z.boolean().nullable(),
            deliverContactsRequirePhone: z.boolean().nullable(),
            deliverLiveNumber: z.boolean().nullable(),
          }),
        ),
    ).nullable(),
    delivery: zodAddUndefinedUnionProps(
      z.union([
        z.object({
          type: z.literal("LassoSales"),
        }),
        z.object({
          type: z.literal("Excel"),
          completionEmail: z.string().nullable(),
        }),
        z.object({
          type: z.literal("Integration"),
          exportSystem: z.string().nullable(),
          exportInput: z.record(z.string(), z.string()).nullable(),
          exportMode: z.enum(["SalesAutomatic", "SalesManual"]).nullable(),
        }),
      ]),
    ).nullable(),
    isTrialPeriod: z.boolean(),
    canSendContactsToIntegration: z.boolean(),
    canSendLiveNumbersToIntegration: z.boolean(),
    completedStepIds: stringSetCodec,
  }),
);

const migrationManager = createMigrationManager(newCampaignStateSchemaBase)
  // v2 example
  .addMigration(
    (schema) =>
      withVersionNumber(2, schema).extend({
        // newProperty: z.string().nullable(),
      }),
    (data) => ({
      ...data,
      __version: 2 as const,
      // newProperty: "Hey there!",
    }),
  )
  .addMigration(
    (schema) =>
      withVersionNumber(3, schema).extend({
        lastVisitedStepMap: lastVisitedStepMapCodec.readonly(),
      }),
    (data) => ({
      ...data,
      __version: 3 as const,
      lastVisitedStepMap: new Map(),
    }),
  );

const fromSerializable = (
  serializableState: z.output<typeof migrationManager.schema>,
) => omit(serializableState, ["__version"]);

export type NewCampaignState = ReturnType<typeof fromSerializable>;

export type NewCampaignStateDelivery = NonNullable<
  NewCampaignState["delivery"]
>;

export type NewCampaignStatePlanning = NonNullable<
  NewCampaignState["planning"]
>;

const toSerializable = (
  state: NewCampaignState,
): z.input<typeof migrationManager.schema> => {
  try {
    return migrationManager.schema.encode({
      ...state,
      // Add the current version to the serialized data, so that we can detect it when deserializing and apply the necessary migrations.
      __version: migrationManager.schema.shape.__version.value,
    });
  } catch (error) {
    // This means that something in our zod schema is invalid. Log the error and capture it in Sentry.
    console.error("Failed to make NewCampaignState serializable", {
      state,
      error,
    });
    Sentry.captureMessage("Failed to make NewCampaignState serializable", {
      level: "error",
      extra: {
        state,
        error,
      },
    });
    throw error;
  }
};

const defaultFilters: (
  | QueryFilter
  | EventQueryFilter
  | DepartmentSizeQueryFilter
)[] = [
  {
    ...filterCategories.basic.filters.companyStatus.filters.value!,
    operator: "Equal",
    values: ["Aktiv"],
  },
  {
    ...filterCategories.basic.filters.excludeHolding.filters.value!,
    operator: "Equal",
    values: ["false"],
  },
  {
    ...filterCategories.contact.filters.liveNumber.filters.value!,
    operator: "Equal",
    values: ["true"],
  },
];

export const newDefaultConfig = {
  quality: 1,
  priority: 1,
  contactConfiguration: null,
  idealLeadShare: 100,
  estimatedRemaining: null,
  enabled: true,
  query: {
    limit: null,
    offset: 0,
    descendingOrder: false,
    orderBy: null,
    filters: defaultFilters,
  },
};

export const getEmptyConfig = (
  quality: number,
  priority: number,
): Configuration => ({
  ...newDefaultConfig,
  quality,
  priority,
  enabled: false,
  query: {
    ...newDefaultConfig.query,
    filters: [],
  },
});

const defaultNewCampaignState: NewCampaignState = {
  draftId: "",
  sessionId: "",
  isSetup: true,
  configurations: [
    { ...newDefaultConfig },
    getEmptyConfig(2, 2),
    getEmptyConfig(3, 3),
  ],
  isTrialPeriod: false,
  canSendContactsToIntegration: false,
  canSendLiveNumbersToIntegration: false,
  name: null,
  responsibleId: null,
  stopLists: [],
  planning: null,
  delivery: null,
  completedStepIds: new Set(),
  lastVisitedStepMap: new Map(),
};

const newCampaignStateJSONStorage = (() => {
  const baseStorage = createJSONStorage();
  const { subscribe: baseSubscribe } = baseStorage;

  const parseValue = (
    key: string,
    value: unknown,
    initialValue: NewCampaignState,
  ): NewCampaignState => {
    if (!value) return initialValue;

    try {
      const serializableValue = migrationManager.migrate(value);
      return fromSerializable(serializableValue);
    } catch (error) {
      console.error(
        "Failed to parse NewCampaignState from storage, returning initial value",
        {
          value,
          error,
        },
      );
      baseStorage.removeItem(key);
      return initialValue;
    }
  };

  const storage: SyncStorage<NewCampaignState> = {
    setItem: (key, value) => {
      try {
        const serializableValue = toSerializable(value);
        return baseStorage.setItem(key, serializableValue);
      } catch (error) {
        console.error(
          "Failed to make NewCampaignState serializable for storage, not saving to storage",
          {
            value,
            error,
          },
        );
      }
    },
    getItem: (key, initialValue) => {
      const value = baseStorage.getItem(key, null);
      return parseValue(key, value, initialValue);
    },
    removeItem: (key) => {
      return baseStorage.removeItem(key);
    },
    subscribe:
      baseSubscribe &&
      ((key, callback, initialValue) => {
        return baseSubscribe(
          key,
          (value) => callback(parseValue(key, value, initialValue)),
          initialValue,
        );
      }),
  };

  return storage;
})();

export const newCampaignStateAtom = atomWithStorage<NewCampaignState>(
  "sales.lassox.com/newCampaignState",
  defaultNewCampaignState,
  newCampaignStateJSONStorage,
  {
    getOnInit: true,
  },
);
